import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Eye, Code, Edit, Trash2, Settings, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { CommunicationTemplateCodeModal } from "./CommunicationTemplateCodeModal";
import { CommunicationTemplateForm } from "./CommunicationTemplateForm";
import { TemplateGettingStarted } from "./TemplateGettingStarted";
import type { CommunicationTemplate } from "@/types/communication-template";
const formatColors = {
  handlebars: 'bg-blue-100 text-blue-800',
  react_email: 'bg-green-100 text-green-800',
  hybrid: 'bg-purple-100 text-purple-800'
};
const categoryColors = {
  booking: 'bg-orange-100 text-orange-800',
  notification: 'bg-cyan-100 text-cyan-800',
  payment: 'bg-emerald-100 text-emerald-800',
  system: 'bg-gray-100 text-gray-800'
};
export function CommunicationTemplatesManager() {
  const [selectedTemplate, setSelectedTemplate] = useState<CommunicationTemplate | null>(null);
  const [codeViewOpen, setCodeViewOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CommunicationTemplate | null>(null);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    data: templates,
    isLoading
  } = useQuery({
    queryKey: ['communication-templates'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('communication_templates').select('*').order('category', {
        ascending: true
      }).order('name', {
        ascending: true
      });
      if (error) throw error;
      return data.map((item: any) => ({
        ...item,
        description: item.description || '',
        template_format: item.template_format as 'handlebars' | 'react_email' | 'hybrid' || 'react_email',
        email_subject_template: item.email_subject_template || item.subject_template || '',
        email_content_template: item.email_content_template || item.content_template || '',
        sms_content_template: item.sms_content_template || item.sms_template,
        webhook_payload_template: item.webhook_payload_template || {},
        webhook_headers: item.webhook_headers || {},
        sample_data: item.sample_data || {},
        variables: item.variables || {},
        // Keep deprecated fields for backward compatibility
        subject_template: item.subject_template,
        content_template: item.content_template,
        supported_channels: item.supported_channels || ['email']
      })) as CommunicationTemplate[];
    }
  });
  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const {
        error
      } = await supabase.from('communication_templates').delete().eq('id', templateId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['communication-templates']
      });
      toast({
        title: "Success",
        description: "Template deleted successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  const toggleActiveMutation = useMutation({
    mutationFn: async ({
      templateId,
      isActive
    }: {
      templateId: string;
      isActive: boolean;
    }) => {
      const {
        error
      } = await supabase.from('communication_templates').update({
        is_active: !isActive
      }).eq('id', templateId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['communication-templates']
      });
      toast({
        title: "Success",
        description: "Template status updated"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  const duplicateTemplateMutation = useMutation({
    mutationFn: async (template: CommunicationTemplate) => {
      const newName = prompt('Enter a name for the duplicated template:', `${template.name} (Copy)`);
      if (!newName) throw new Error('Duplication cancelled');
      const {
        error
      } = await supabase.from('communication_templates').insert({
        name: newName,
        category: template.category,
        description: template.description,
        template_format: template.template_format,
        react_email_template: template.react_email_template,
        subject_template: template.subject_template,
        content_template: template.content_template,
        sms_content_template: template.sms_content_template,
        supported_channels: template.supported_channels,
        variables: template.variables,
        sample_data: template.sample_data,
        is_active: false // Start as inactive
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['communication-templates']
      });
      toast({
        title: "Success",
        description: "Template duplicated successfully"
      });
    },
    onError: (error: any) => {
      if (error.message !== 'Duplication cancelled') {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });
  const handleCodeView = (template: CommunicationTemplate) => {
    setSelectedTemplate(template);
    setCodeViewOpen(true);
  };
  const handleEdit = (template: CommunicationTemplate) => {
    setEditingTemplate(template);
    setFormOpen(true);
  };
  const handleDelete = (template: CommunicationTemplate) => {
    if (confirm(`Are you sure you want to delete "${template.name}"?`)) {
      deleteTemplateMutation.mutate(template.id);
    }
  };
  const handleToggleActive = (template: CommunicationTemplate) => {
    toggleActiveMutation.mutate({
      templateId: template.id,
      isActive: template.is_active
    });
  };
  const handleDuplicate = (template: CommunicationTemplate) => {
    duplicateTemplateMutation.mutate(template);
  };
  if (isLoading) {
    return <div className="space-y-4">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 w-full" />)}
      </div>;
  }
  const groupedTemplates = templates?.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, CommunicationTemplate[]>) || {};
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Communication Templates</h3>
          <p className="text-sm text-muted-foreground">
            Channel-agnostic templates for Email, SMS, and Webhook communications
          </p>
        </div>
        <Button onClick={() => {
        setEditingTemplate(null);
        setFormOpen(true);
      }}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {Object.keys(groupedTemplates).length === 0 ? <TemplateGettingStarted onCreateTemplate={() => {
      setEditingTemplate(null);
      setFormOpen(true);
    }} onViewExisting={() => {}} /> : <div className="space-y-6">
          {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => <div key={category} className="space-y-4">
              <h4 className="text-md font-medium capitalize">{category} Templates</h4>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {categoryTemplates.map(template => <Card key={template.id} className={!template.is_active ? 'opacity-60' : ''}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1">
                          <CardTitle className="text-sm">{template.name}</CardTitle>
                          <CardDescription className="text-xs">
                            {template.description}
                          </CardDescription>
                          {/* Channel Readiness Badges */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {template.email_content_template && <Badge variant="default" className="text-xs">
                                Email ✓
                              </Badge>}
                            {template.sms_content_template && <Badge variant="default" className="text-xs">
                                Message ✓
                              </Badge>}
                            {template.webhook_payload_template && Object.keys(template.webhook_payload_template).length > 0 && <Badge variant="default" className="text-xs">
                                Webhook ✓
                              </Badge>}
                            {template.whatsapp_content_template && <Badge variant="default" className="text-xs">
                                WhatsApp ✓
                              </Badge>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 ml-2">
                          
                          <Badge variant="outline" className={categoryColors[template.category as keyof typeof categoryColors] || 'bg-gray-100 text-gray-800'}>
                            {template.category}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleCodeView(template)}>
                            <Code className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDuplicate(template)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(template)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button variant={template.is_active ? "default" : "secondary"} size="sm" onClick={() => handleToggleActive(template)}>
                          <Settings className="w-4 h-4 mr-1" />
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>
            </div>)}
        </div>}

      {selectedTemplate && <CommunicationTemplateCodeModal template={selectedTemplate} isOpen={codeViewOpen} onClose={() => setCodeViewOpen(false)} />}

      <CommunicationTemplateForm template={editingTemplate} isOpen={formOpen} onClose={() => {
      setFormOpen(false);
      setEditingTemplate(null);
    }} onSuccess={() => {
      queryClient.invalidateQueries({
        queryKey: ['communication-templates']
      });
      setFormOpen(false);
      setEditingTemplate(null);
    }} />
    </div>;
}